import { useMutation } from '@tanstack/react-query';
import { linkOptions, useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { useState } from 'react';

import { Role } from '../../../../types/Role';
import { BreadcrumbLink, Breadcrumbs } from '../../../cdl/Breadcrumbs/Breadcrumbs';
import { Button } from '../../../cdl/Button/Button';
import { Tabs } from '../../../cdl/Tabs/Tabs';
import { Text } from '../../../cdl/Text/Text';
import { sendVesselRequisitionsForCustomer } from '../../../common/api/clients/vesselRequisition.api';
import { AdvancedError } from '../../../common/Error/AdvancedError';
import { translate } from '../../../common/helpers/translate.helper';
import { useCustomerDetails } from '../../../common/hooks/useCustomerDetails';
import { useDocumentTitle } from '../../../common/hooks/useDocumentTitle';
import { useRole } from '../../../common/hooks/useRole';
import { IconDownload } from '../../../common/icons/cdl/Download';
import { LoadingIndicator } from '../../../common/LoadingIndicator/LoadingIndicator';
import { Page } from '../../../common/Page/Page';
import { Box } from '../../../common/ui/Box';
import { RequisitionAction } from '../../../common/VesselRequisition/types/RequisitionAction';
import { RequisitionScope } from '../../../common/VesselRequisition/types/RequisitionScope';
import { VesselRequisitionModal } from '../../../common/VesselRequisition/VesselRequisitionModal';
import { CompanyTabs } from '../../common/CompanyTabs';

import { CustomerDetailsPageTabContent } from './tabs/CustomerDetailsPageTabContent';
import { useVesselProductsCustomerDownload } from './useVesselProductsCustomerDownload';
import { useVesselRequisitionCustomerDownload } from './useVesselRequisitionCustomerDownload';

const getBreadcrumbLinks = (groupId: string, role: Role): BreadcrumbLink[] => {
    const settings = { label: translate('global.settings'), linkOptions: linkOptions({ to: '/settings' }) };
    const groupManagement = {
        label: translate('settings.groupManagement.title'),
        linkOptions: linkOptions({ to: '/customer-groups/$groupId', params: { groupId } }),
    };

    if (role.isSuperAdmin()) {
        return [
            { label: translate('page.customers'), linkOptions: linkOptions({ to: '/customer-groups' }) },
            groupManagement,
        ];
    }

    if (role.hasAdminRights()) {
        return [settings, groupManagement];
    }

    return [
        settings,
        {
            label: translate('settings.companyManagement.title'),
            linkOptions: linkOptions({ to: '/customer-groups/$groupId/companies', params: { groupId } }),
        },
    ];
};

export const CustomerDetailPage = () => {
    const [showRequisitionModal, setShowRequisitionModal] = useState(false);
    const role = useRole();
    const sendRequisitionsMutation = useMutation({
        mutationFn: sendVesselRequisitionsForCustomer,
        retry: false,
    });
    const { companyId, groupId } = useParams({ from: '/_app/customer-groups/$groupId/companies/$companyId' });

    const customerDetailQuery = useCustomerDetails(companyId);
    const downloadVesselRequisitions = useVesselRequisitionCustomerDownload();
    const downloadVesselProducts = useVesselProductsCustomerDownload();

    useDocumentTitle(customerDetailQuery.data?.name || translate('company.title'));

    const { tab } = useSearch({ from: '/_app/customer-groups/$groupId/companies/$companyId' });
    const navigate = useNavigate({ from: '/customer-groups/$groupId/companies/$companyId' });

    if (customerDetailQuery.isPending) {
        return (
            <Page>
                <LoadingIndicator variant="page" />
            </Page>
        );
    }

    if (customerDetailQuery.isError) {
        return (
            <Page>
                <AdvancedError />
            </Page>
        );
    }

    const customer = customerDetailQuery.data!;

    const totals = {
        [CompanyTabs.DETAILS]: null,
        [CompanyTabs.SETTINGS]: null,
        [CompanyTabs.COMMUNICATION_PREFERENCES]: null,
    };

    return (
        <Page>
            {showRequisitionModal ? (
                <VesselRequisitionModal
                    scope={RequisitionScope.COMPANY}
                    onDismiss={() => setShowRequisitionModal(false)}
                    onSubmit={async (options, action) => {
                        if (action === RequisitionAction.SEND) {
                            const response = await sendRequisitionsMutation.mutateAsync({ id: companyId, options });
                            return response.data;
                        }
                        await downloadVesselRequisitions(companyId, options);
                    }}
                />
            ) : null}
            <Box display="flex" flexDirection="column" gap={16}>
                <Box display="flex" flexDirection="column" gap={4}>
                    <Breadcrumbs links={getBreadcrumbLinks(groupId, role)} />

                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Text as="h1" variant="headline">
                            {translate('company.title')}
                        </Text>
                        <Box display="flex" gap={8}>
                            <Button onClick={() => downloadVesselProducts(companyId)} LeadingIcon={IconDownload}>
                                {translate('customers.vesselProducts.downloadButton')}
                            </Button>
                            {role.isSuperAdmin() ? (
                                <Button onClick={() => setShowRequisitionModal(true)}>
                                    {translate('customers.requisitionForm.downloadButton')}
                                </Button>
                            ) : null}
                        </Box>
                    </Box>

                    <Text variant="body" color="foreground.subtle">
                        {customer.name}
                    </Text>
                </Box>

                <Box display="flex" flexDirection="column" gap={32}>
                    <Tabs
                        selectedContext={tab}
                        onSelect={(newTab: CompanyTabs) => navigate({ search: (prev) => ({ ...prev, tab: newTab }) })}
                        totals={totals}
                    />

                    <CustomerDetailsPageTabContent tab={tab} customer={customer} />
                </Box>
            </Box>
        </Page>
    );
};
